import type { CreateToastOptions } from 'redux/modules/toasts.types';
import { addToast, dismissToast, persistToast } from 'redux/modules/toasts.types';
import type { Thunk } from 'types/redux';
import uuidv4 from 'utils/uuid';
import { getUserVerboseToastsFromLocalStorage, LogLevel } from '~utils/debug';
export { dismissToast, persistToast };

/**
 * Show a Toast message.
 * @param logLevel     - NOT related to `type` or visual presentation,
 *                       but allows dispatching "unguarded" toasts for dev/internal visibility.
 *                       Info (Warning+Error) is default.
 *                       Verbose (and Debug) can be enabled via Timely DevTools.
 * @param dismissAfter - How long to show the toast before auto dismissing (varies by type)
 *                       success: 5s, info: 7s, warning: 10s, error: 15s, default: 7s
 */
export const showToast =
  (toast: CreateToastOptions): Thunk =>
  dispatch => {
    const showVerbose = getUserVerboseToastsFromLocalStorage();
    const currentLogLevel = showVerbose ? LogLevel.Verbose : LogLevel.Info;

    // Developer-facing "debug" toasts are expressed via `logLevel: Verbose` (the
    // existing mechanism) — they're hidden unless verbose toasts are enabled
    // (DevTools → Show verbose toasts). Default to Info when the caller omits it.
    const requiredLogLevel = toast?.logLevel || LogLevel.Info;

    /**
     * @see LogLevel Toasts at logLevel higher than currentLogLevel are not shown.
     */
    if (requiredLogLevel > currentLogLevel) {
      return;
    }

    // Set duration based on toast type
    const type = toast.type || 'info';
    let defaultDismissAfter = 7000; // Default for info

    if (type === 'success') {
      defaultDismissAfter = 5000; // 5s for success
    } else if (type === 'warning') {
      defaultDismissAfter = 10000; // 10s for warning
    } else if (type === 'danger') {
      defaultDismissAfter = 15000; // 15s for error
    }

    dispatch(
      addToast({
        title: toast.title,
        action: toast.action,
        dismissAfter: toast.dismissAfter ?? defaultDismissAfter,
        key: uuidv4(),
        message: toast.message,
        type: type,
        // Persist the caller's logLevel so a verbose toast still renders with the
        // "Debug" pill / neutral chrome in Toasts.tsx (its isDebug check keys off
        // logLevel > Info).
        logLevel: toast.logLevel,
      })
    );
  };
